/**
 * DateTime Module - Centralized Timezone-Safe Date/Time Handling
 *
 * This module provides compile-time safety for timezone conversions.
 * It prevents the common mistake of displaying UTC times directly
 * or forgetting to convert to the configured timezone.
 *
 * DESIGN PRINCIPLES:
 * 1. Branded types prevent accidental mixing of UTC vs Local times
 * 2. All conversions are explicit and type-checked at compile time
 * 3. The configured timezone is required (no implicit defaults in core logic)
 * 4. Works in both frontend and backend environments
 *
 * The string-level conversions/validation are delegated to the shared
 * `@bavard/utils` implementation (one source of truth, shared with the server);
 * this module owns the api-client's brands for the web and applies them on top.
 *
 * USAGE:
 * - Database stores UTC (UTCDateTimeString)
 * - Display uses local time (LocalDateTimeString)
 * - Conversion is explicit: utcToLocal(utc, timezone)
 */

import {
	isLocalDateTimeString,
	isUtcDateTimeString,
	isValidTimezone,
	localDateTimeToUtc as localDateTimeToUtcPlain,
	localToUtc as localToUtcPlain,
	utcToLocalDate as utcToLocalDatePlain,
	utcToLocalParts as utcToLocalPartsPlain,
	utcToLocal as utcToLocalPlain,
	utcToLocalTime as utcToLocalTimePlain,
} from "@bavard/utils";
import { z } from "zod";
import type { Brand } from "../common/branded-types";

// ============================================================================
// BRANDED TYPES - Compile-Time Safety for Date/Time Values
// ============================================================================

/**
 * UTC DateTime string (from database).
 * Format: ISO 8601 with Z suffix (e.g., "2026-01-04T08:00:00.000Z")
 *
 * NEVER display this directly to users - always convert to LocalDateTimeString first!
 */
export type UTCDateTimeString = Brand<string, "UTCDateTime">;

/**
 * Local DateTime string (for display in configured timezone).
 * Format: ISO 8601 WITHOUT timezone suffix (e.g., "2026-01-04T11:00:00")
 *
 * This is safe to display to users as it's already in their timezone.
 */
export type LocalDateTimeString = Brand<string, "LocalDateTime">;

/**
 * Date-only string in local timezone.
 * Format: YYYY-MM-DD (e.g., "2026-01-04")
 */
export type LocalDateString = Brand<string, "LocalDate">;

/**
 * Time-only string in local timezone.
 * Format: HH:mm (e.g., "11:00")
 */
export type LocalTimeString = Brand<string, "LocalTime">;

/**
 * Valid IANA timezone identifier.
 * Examples: "America/New_York", "Europe/London", "Asia/Tokyo"
 */
export type Timezone = Brand<string, "Timezone">;

// ============================================================================
// ZOD SCHEMAS - Runtime Validation with Type Safety
// ============================================================================

/**
 * Schema for validating timezone identifiers
 */
export const TimezoneSchema = z
	.string()
	.refine(isValidTimezone, { message: "Invalid IANA timezone identifier" })
	.transform((v) => v as Timezone);

/**
 * Schema for UTC datetime strings (with Z suffix or offset)
 */
export const UTCDateTimeStringSchema = z
	.string()
	.refine(isUtcDateTimeString, {
		message: "UTC datetime must have Z suffix or timezone offset",
	})
	.transform((v) => v as UTCDateTimeString);

/**
 * Schema for local datetime strings (no timezone suffix)
 */
export const LocalDateTimeStringSchema = z
	.string()
	.refine(isLocalDateTimeString, {
		message: "Local datetime must not have timezone suffix",
	})
	.transform((v) => v as LocalDateTimeString);

/**
 * Schema for local date strings (YYYY-MM-DD)
 */
export const LocalDateStringSchema = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
	.transform((v) => v as LocalDateString);

/**
 * Schema for local time strings (HH:mm)
 */
export const LocalTimeStringSchema = z
	.string()
	.regex(/^\d{2}:\d{2}$/, "Time must be in HH:mm format")
	.transform((v) => v as LocalTimeString);

// ============================================================================
// CONVERSION FUNCTIONS - Type-Safe Transformations (delegated to @bavard/utils)
// ============================================================================

/**
 * Convert a UTC datetime to local datetime in the specified timezone.
 * This is the PRIMARY function for displaying dates to users.
 */
export function utcToLocal(
	utc: UTCDateTimeString | Date,
	timezone: Timezone
): LocalDateTimeString {
	return utcToLocalPlain(utc, timezone) as LocalDateTimeString;
}

/**
 * Convert a UTC datetime to date-only string in local timezone.
 */
export function utcToLocalDate(
	utc: UTCDateTimeString | Date,
	timezone: Timezone
): LocalDateString {
	return utcToLocalDatePlain(utc, timezone) as LocalDateString;
}

/**
 * Convert a UTC datetime to time-only string in local timezone.
 */
export function utcToLocalTime(
	utc: UTCDateTimeString | Date,
	timezone: Timezone
): LocalTimeString {
	return utcToLocalTimePlain(utc, timezone) as LocalTimeString;
}

/**
 * Convert a UTC datetime to local date + time parts in the specified timezone.
 */
export function utcToLocalParts(
	utc: UTCDateTimeString | Date,
	timezone: Timezone
): { date: LocalDateString; time: LocalTimeString } {
	const parts = utcToLocalPartsPlain(utc, timezone);
	return {
		date: parts.date as LocalDateString,
		time: parts.time as LocalTimeString,
	};
}

/**
 * Convert local date and time to UTC for storage in database.
 */
export function localToUtc(
	date: LocalDateString,
	time: LocalTimeString,
	timezone: Timezone
): UTCDateTimeString {
	return localToUtcPlain(date, time, timezone) as UTCDateTimeString;
}

/**
 * Convert a local datetime string (no timezone suffix) to UTC for storage.
 */
export function localDateTimeToUtc(
	local: LocalDateTimeString,
	timezone: Timezone
): UTCDateTimeString {
	return localDateTimeToUtcPlain(local, timezone) as UTCDateTimeString;
}

// ============================================================================
// TYPE GUARDS AND ASSERTIONS
// ============================================================================

/**
 * Assert that a string is a valid UTC datetime.
 * Use this when receiving data from external sources.
 */
export function assertUTC(value: string): UTCDateTimeString {
	return UTCDateTimeStringSchema.parse(value);
}

/**
 * Assert that a string is a valid local datetime.
 * Use this when receiving display-ready data.
 */
export function assertLocal(value: string): LocalDateTimeString {
	return LocalDateTimeStringSchema.parse(value);
}

/**
 * Assert that a string is a valid timezone.
 * Use this when receiving timezone config.
 */
export function assertTimezone(value: string): Timezone {
	return TimezoneSchema.parse(value);
}

// ============================================================================
// MARKER TYPE FOR COMPILE-TIME ENFORCEMENT
// ============================================================================

/**
 * Marker type to indicate a value has been timezone-converted.
 * Use this in function return types to enforce conversion at compile time.
 */
export type TimezoneConverted<T> = T & { readonly __timezoneConverted: true };

/**
 * Create a timezone-converted object.
 * This is a compile-time marker that has no runtime cost.
 */
export function markTimezoneConverted<T>(value: T): TimezoneConverted<T> {
	return value as TimezoneConverted<T>;
}
